import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './UnmanagedWordPress.css';

const UnmanagedWordPress = () => {
    const scrollToPricing = (e) => {
        e.preventDefault();
        const pricingSection = document.getElementById('pricing');
        if (pricingSection) {
            const headerOffset = 100;
            const elementPosition = pricingSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <>
            <Helmet>
                {/* Primary Meta Tags */}
                <title>Unmanaged WordPress Hosting - Full Control & Affordable | Epicfinal</title>
                <meta name="title" content="Unmanaged WordPress Hosting - Full Control & Affordable | Epicfinal" />
                <meta name="description" content="Self-managed WordPress hosting from $5.99/mo. Full root control, NVMe SSD, free SSL, Cloudflare CDN & 1-click install. Perfect for developers & agencies who want complete flexibility." />
                <meta name="keywords" content="unmanaged WordPress, self-managed WordPress, DIY WordPress hosting, WordPress hosting for developers, cheap WordPress hosting, affordable WordPress, WordPress full control, developer WordPress hosting, agency WordPress hosting, WordPress multisite hosting" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="author" content="Epicfinal" />
                <link rel="canonical" href="https://epicfinal.com/unmanaged-wordpress" />

                {/* Language & Locale */}
                <html lang="en" />
                <meta property="og:locale" content="en_US" />
                <link rel="alternate" href="https://epicfinal.com/unmanaged-wordpress" hrefLang="en" />
                <link rel="alternate" href="https://epicfinal.com/unmanaged-wordpress" hrefLang="x-default" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://epicfinal.com/unmanaged-wordpress" />
                <meta property="og:title" content="Unmanaged WordPress Hosting - Full Control | Epicfinal" />
                <meta property="og:description" content="Self-managed WordPress hosting from $5.99/mo. Full control, NVMe SSD, free SSL & Cloudflare CDN for developers." />
                <meta property="og:image" content="https://epicfinal.com/android-chrome-512x512.png" />
                <meta property="og:image:alt" content="Epicfinal Unmanaged WordPress - Developer-Friendly Hosting" />
                <meta property="og:site_name" content="Epicfinal" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://epicfinal.com/unmanaged-wordpress" />
                <meta name="twitter:title" content="Unmanaged WordPress Hosting | Epicfinal" />
                <meta name="twitter:description" content="Self-managed WordPress from $5.99/mo. Full control for developers & agencies." />
                <meta name="twitter:image" content="https://epicfinal.com/android-chrome-512x512.png" />

                {/* Schema.org Structured Data */}
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Unmanaged WordPress Hosting - Full Control & Affordable",
                        "description": "Self-managed WordPress hosting for developers and agencies with full control, NVMe SSD storage, and affordable pricing.",
                        "url": "https://epicfinal.com/unmanaged-wordpress",
                        "provider": {"@type": "Organization", "name": "Epicfinal", "url": "https://epicfinal.com"},
                        "breadcrumb": {
                            "@type": "BreadcrumbList",
                            "itemListElement": [
                                {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://epicfinal.com"},
                                {"@type": "ListItem", "position": 2, "name": "Unmanaged WordPress", "item": "https://epicfinal.com/unmanaged-wordpress"}
                            ]
                        }
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": "Epicfinal Unmanaged WordPress Hosting",
                        "description": "Self-managed WordPress hosting with full control, NVMe SSD storage, and developer-friendly features",
                        "brand": {"@type": "Brand", "name": "Epicfinal"},
                        "category": "Web Hosting",
                        "offers": [
                            {"@type": "Offer", "name": "epicfinal Starter", "price": "5.99", "priceCurrency": "USD", "priceValidUntil": "2025-12-31", "availability": "https://schema.org/InStock"},
                            {"@type": "Offer", "name": "epicfinal Cloud – Web Pro", "price": "8.99", "priceCurrency": "USD", "priceValidUntil": "2025-12-31", "availability": "https://schema.org/InStock"},
                            {"@type": "Offer", "name": "epicfinal Business", "price": "15.99", "priceCurrency": "USD", "priceValidUntil": "2025-12-31", "availability": "https://schema.org/InStock"}
                        ],
                        "aggregateRating": {"@type": "AggregateRating", "ratingValue": "4.7", "reviewCount": "85", "bestRating": "5", "worstRating": "1"}
                    }
                `}</script>
            </Helmet>

            <div className="unmanaged-wp-page">
                {/* Hero Section */}
                <section className="unmanaged-wp-hero">
                    <div className="container">
                        <div className="unmanaged-wp-hero-content">
                            <h1 className="unmanaged-wp-hero-title">
                                Unmanaged WordPress Hosting
                            </h1>
                            <p className="unmanaged-wp-hero-subtitle">
                                Full control, affordable hosting for WordPress
                            </p>
                            <p className="unmanaged-wp-hero-description">
                                Perfect for developers and tech-savvy users who want complete control over their WordPress environment. You manage, you customize, you decide.
                            </p>
                            <div className="unmanaged-wp-hero-buttons">
                                <a href="#pricing" onClick={scrollToPricing} className="btn-unmanaged-wp btn-primary">
                                    View Plans & Pricing
                                </a>
                                <Link to="/contact1" className="btn-unmanaged-wp btn-secondary">
                                    Talk to Expert
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="unmanaged-wp-features">
                    <div className="container">
                        <div className="unmanaged-wp-section-header">
                            <h2>Why Choose Unmanaged WordPress?</h2>
                            <p>Maximum flexibility at an affordable price</p>
                        </div>
                        <div className="unmanaged-wp-features-grid">
                            <div className="unmanaged-wp-feature-card">
                                <div className="unmanaged-wp-feature-icon">
                                    <i className="las la-cogs"></i>
                                </div>
                                <h3>Full Control</h3>
                                <p>Complete access to customize and configure your WordPress exactly as you need</p>
                            </div>
                            <div className="unmanaged-wp-feature-card">
                                <div className="unmanaged-wp-feature-icon">
                                    <i className="las la-dollar-sign"></i>
                                </div>
                                <h3>Cost-Effective</h3>
                                <p>Lower prices because you manage your own WordPress installation and updates</p>
                            </div>
                            <div className="unmanaged-wp-feature-card">
                                <div className="unmanaged-wp-feature-icon">
                                    <i className="las la-shield-alt"></i>
                                </div>
                                <h3>Free SSL Certificate</h3>
                                <p>Secure your website with free SSL encryption included in all plans</p>
                            </div>
                            <div className="unmanaged-wp-feature-card">
                                <div className="unmanaged-wp-feature-icon">
                                    <i className="las la-tachometer-alt"></i>
                                </div>
                                <h3>High Performance</h3>
                                <p>NVMe SSD storage and optimized servers ensure fast loading times</p>
                            </div>
                            <div className="unmanaged-wp-feature-card">
                                <div className="unmanaged-wp-feature-icon">
                                    <i className="las la-headset"></i>
                                </div>
                                <h3>Support</h3>
                                <p>Server-level support available when you need infrastructure help</p>
                            </div>
                            <div className="unmanaged-wp-feature-card">
                                <div className="unmanaged-wp-feature-icon">
                                    <i className="las la-rocket"></i>
                                </div>
                                <h3>1-Click Install</h3>
                                <p>Quick WordPress installation to get you started fast</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Pricing Plans Section */}
                <section className="unmanaged-wp-pricing" id="pricing">
                    <div className="container">
                        <div className="unmanaged-wp-section-header">
                            <h2>Choose Your Plan</h2>
                            <p>Self-managed WordPress hosting plans</p>
                        </div>
                        <div className="unmanaged-wp-pricing-grid">
                            {/* Starter Plan */}
                            <div className="unmanaged-wp-pricing-card">
                                <div className="unmanaged-wp-pricing-header">
                                    <h3>epicfinal Starter</h3>
                                    <p className="unmanaged-wp-pricing-tagline">Designed for small businesses, freelancers and personal projects</p>
                                </div>
                                <div className="unmanaged-wp-pricing-price">
                                    <span className="unmanaged-wp-price-amount">$5.99</span>
                                    <span className="unmanaged-wp-price-period">USD / Monthly</span>
                                </div>
                                <ul className="unmanaged-wp-pricing-features">
                                    <li><i className="las la-check"></i> <strong>1 Website</strong></li>
                                    <li><i className="las la-check"></i> 5GB NVMe SSD Storage</li>
                                    <li><i className="las la-check"></i> Unmetered Traffic (Fair Use)</li>
                                    <li><i className="las la-check"></i> Email & Databases</li>
                                    <li><i className="las la-check"></i> Up to 3 Databases</li>
                                    <li><i className="las la-check"></i> WAF – Web Application Firewall</li>
                                    <li><i className="las la-check"></i> Malware Protection</li>
                                    <li><i className="las la-check"></i> Free SSL Certificates</li>
                                    <li><i className="las la-check"></i> Cloudflare Integration</li>
                                    <li><i className="las la-check"></i> User-Friendly Control Panel</li>
                                    <li><i className="las la-check"></i> 1-Click WordPress Install</li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/s1-web-starter" 
                                   className="unmanaged-wp-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started
                                </a>
                            </div>

                            {/* Pro Plan */}
                            <div className="unmanaged-wp-pricing-card featured">
                                <div className="unmanaged-wp-pricing-badge">Most Popular</div>
                                <div className="unmanaged-wp-pricing-header">
                                    <h3>epicfinal Cloud – Web Pro</h3>
                                    <p className="unmanaged-wp-pricing-tagline">Advanced Hosting for Growing Websites</p>
                                </div>
                                <div className="unmanaged-wp-pricing-price">
                                    <span className="unmanaged-wp-price-amount">$8.99</span>
                                    <span className="unmanaged-wp-price-period">USD / Monthly</span>
                                </div>
                                <ul className="unmanaged-wp-pricing-features">
                                    <li><i className="las la-check"></i> <strong>Up to 3 Separate Websites</strong></li>
                                    <li><i className="las la-check"></i> 15GB NVMe SSD Storage</li>
                                    <li><i className="las la-check"></i> Unmetered Traffic (Fair Use)</li>
                                    <li><i className="las la-check"></i> WAF & Protection Against Malware</li>
                                    <li><i className="las la-check"></i> Anti-Spam Protection for Email</li>
                                    <li><i className="las la-check"></i> Free SSL for All Domains</li>
                                    <li><i className="las la-check"></i> Cloudflare Integration</li>
                                    <li><i className="las la-check"></i> 1-Click WordPress Install</li>
                                    <li><i className="las la-check"></i> Caching for Faster Page Loads</li>
                                    <li><i className="las la-check"></i> Access to Logs & Statistics</li>
                                    <li><i className="las la-check"></i> Self-Managed Updates</li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/epicfinal-cloud-web-pro" 
                                   className="unmanaged-wp-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started
                                </a>
                            </div>

                            {/* Business Plan */}
                            <div className="unmanaged-wp-pricing-card">
                                <div className="unmanaged-wp-pricing-header">
                                    <h3>epicfinal Business</h3>
                                    <p className="unmanaged-wp-pricing-tagline">Host multiple client projects under one powerful platform</p>
                                </div>
                                <div className="unmanaged-wp-pricing-price">
                                    <span className="unmanaged-wp-price-amount">$15.99</span>
                                    <span className="unmanaged-wp-price-period">USD / Monthly</span>
                                </div>
                                <ul className="unmanaged-wp-pricing-features">
                                    <li><i className="las la-check"></i> <strong>Up to 10 Websites</strong></li>
                                    <li><i className="las la-check"></i> 40GB NVMe SSD Storage</li>
                                    <li><i className="las la-check"></i> Unmetered Traffic (~150k Visits/Month)</li>
                                    <li><i className="las la-check"></i> Ideal for WooCommerce Stores</li>
                                    <li><i className="las la-check"></i> WordPress Multisite Support</li>
                                    <li><i className="las la-check"></i> Perfect for Agencies</li>
                                    <li><i className="las la-check"></i> Large Projects Support</li>
                                    <li><i className="las la-check"></i> Priority Support</li>
                                    <li><i className="las la-check"></i> All Pro Features Included</li>
                                    <li><i className="las la-check"></i> Advanced Security Features</li>
                                    <li><i className="las la-check"></i> Full Control & Flexibility</li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/epicfinal-business" 
                                   className="unmanaged-wp-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Perfect For Section */}
                <section className="unmanaged-wp-perfect">
                    <div className="container">
                        <div className="unmanaged-wp-section-header">
                            <h2>Perfect For</h2>
                            <p>Who should choose unmanaged WordPress?</p>
                        </div>
                        <div className="unmanaged-wp-perfect-grid">
                            <div className="unmanaged-wp-perfect-card">
                                <div className="unmanaged-wp-perfect-icon">
                                    <i className="las la-code"></i>
                                </div>
                                <h3>Developers</h3>
                                <p>Full control to customize and optimize WordPress exactly as needed</p>
                            </div>
                            <div className="unmanaged-wp-perfect-card">
                                <div className="unmanaged-wp-perfect-icon">
                                    <i className="las la-users-cog"></i>
                                </div>
                                <h3>Agencies</h3>
                                <p>Manage multiple client sites with complete flexibility</p>
                            </div>
                            <div className="unmanaged-wp-perfect-card">
                                <div className="unmanaged-wp-perfect-icon">
                                    <i className="las la-user-graduate"></i>
                                </div>
                                <h3>Tech-Savvy Users</h3>
                                <p>Comfortable managing WordPress updates and maintenance yourself</p>
                            </div>
                            <div className="unmanaged-wp-perfect-card">
                                <div className="unmanaged-wp-perfect-icon">
                                    <i className="las la-piggy-bank"></i>
                                </div>
                                <h3>Budget-Conscious</h3>
                                <p>Get professional hosting at a lower price by managing it yourself</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="unmanaged-wp-cta">
                    <div className="container">
                        <div className="unmanaged-wp-cta-content">
                            <h2>Ready to Get Started?</h2>
                            <p>Launch your self-managed WordPress site today</p>
                            <div className="unmanaged-wp-cta-buttons">
                                <Link to="#pricing" className="btn-unmanaged-wp btn-light">
                                    Choose Your Plan
                                </Link>
                                <Link to="/he/wordpress" className="btn-unmanaged-wp btn-outline">
                                    Want Managed WordPress?
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default UnmanagedWordPress;

